import {
	PAGES,
	Proposition,
	PropositionProductTypeValues,
	PropositionStatus,
	PropositionStatusValues,
} from "@dune/types";
import { PaginatedDataTable, useSidebar } from "@dune/ui";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateProposition } from "../../api/mutations/propositions.mutations";
import {
	usePropositions,
	usePropositionsCounts,
} from "../../api/queries/propositions.queries";
import ConfirmationDialog from "../../component/common/ConfirmationDialog";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { usePropositionFilters } from "../../hooks/propositions/usePropositionUtilities";
import { PropositionFilters } from "./components/PropositionsTabs";
import { propositionsColumns } from "./components/propositionsColumns";

const PropositionsPage: React.FC = () => {
	const navigate = useNavigate();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const { filters, updateFilters } = usePropositionFilters();
	const updatePropositionMutation = useUpdateProposition();

	const [searchInput, setSearchInput] = useState(filters.search || "");
	const [isSansSuiteModalOpen, setIsSansSuiteModalOpen] = useState(false);
	const [selectedPropositionId, setSelectedPropositionId] = useState<
		number | null
	>(null);

	const {
		data: propositionsResponse,
		isLoading,
		error,
	} = usePropositions(filters);

	const { data: countsData } = usePropositionsCounts();

	const propositions = propositionsResponse?.data || [];
	const paginationMeta = propositionsResponse?.meta || {
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};
	const statusCounts = countsData?.statusCounts || {};
	const productTypeCounts = countsData?.productTypeCounts || {};

	const handlePageChange = (page: number) => {
		updateFilters({ page });
	};

	const handleLimitChange = (limit: number) => {
		updateFilters({ limit, page: 1 });
	};

	const handleSearchChange = (value: string) => {
		setSearchInput(value);
		updateFilters({ search: value || undefined, page: 1 });
	};

	const handleStatusFilter = (status: PropositionStatusValues | undefined) => {
		updateFilters({
			status,
			page: 1,
		});
	};

	const handleProductTypeFilter = (
		productType: PropositionProductTypeValues | undefined,
	) => {
		updateFilters({
			productType,
			page: 1,
		});
	};

	const handleRowClick = (proposition: Proposition) => {
		navigate(PAGES.PROPOSALS.show.withParams(proposition.id.toString()));
	};

	const confirmPasserSansSuite = async () => {
		if (!selectedPropositionId) return;

		try {
			await updatePropositionMutation.mutateAsync({
				id: selectedPropositionId,
				status: PropositionStatus.SANS_SUITE,
			});
			setIsSansSuiteModalOpen(false);
			setSelectedPropositionId(null);
		} catch (error) {
			console.error("Error updating proposition:", error);
		}
	};

	const columns = propositionsColumns();

	if (error) {
		return (
			<div className="flex items-center justify-center p-8">
				<div className="text-red-600">
					Erreur lors du chargement des propositions
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayoutWithTabs title="Propositions" tabContent={{}}>
				<div className="space-y-6 flex-1 min-h-0 flex flex-col">
					{/* Filters */}
					<div
						className={`flex flex-col gap-4 w-full ${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}
					>
						<PropositionFilters
							statusFilter={filters.status}
							productTypeFilter={filters.productType}
							onStatusFilterChange={handleStatusFilter}
							onProductTypeFilterChange={handleProductTypeFilter}
							statusCounts={statusCounts}
							productTypeCounts={productTypeCounts}
							searchInput={searchInput}
							onSearchChange={handleSearchChange}
						/>
					</div>

					{/* Table */}
					<div className="flex-1 min-h-0 w-full overflow-auto">
						<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
							<PaginatedDataTable
								data={propositions}
								columns={columns}
								meta={paginationMeta}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
								isLoading={isLoading}
								onRowClick={handleRowClick}
								noResultsText="Aucune proposition trouvée"
							/>
						</div>
					</div>
				</div>
			</PageLayoutWithTabs>

			<ConfirmationDialog
				open={isSansSuiteModalOpen}
				onOpenChange={setIsSansSuiteModalOpen}
				title="Passer la proposition sans suite"
				description="Êtes-vous sûr de vouloir passer cette proposition sans suite ? Cette action ne peut pas être annulée."
				confirmText="Passer sans suite"
				cancelText="Annuler"
				variant="default"
				onConfirm={confirmPasserSansSuite}
				isLoading={updatePropositionMutation.isPending}
			/>
		</>
	);
};

export default PropositionsPage;
